import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  AzioneMassaEsito,
  FeedStato,
  GeneraAttivitaEsito,
  VoceFeedAdmin,
  VoceFeedAdminUpdate,
  VoceFeedManualeCreate,
} from "@/types/api";

export function useAdminFeed(filter: { stato?: FeedStato; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (filter.stato) qs.set("stato", filter.stato);
  if (filter.limit) qs.set("limit", String(filter.limit));
  const search = qs.toString();
  return useQuery({
    queryKey: ["admin-feed", filter.stato ?? "tutti", filter.limit ?? 50],
    queryFn: () =>
      apiFetch<VoceFeedAdmin[]>(
        `/api/v1/admin/feed${search ? `?${search}` : ""}`
      ),
    staleTime: 15_000,
  });
}

interface UpdateArgs {
  id: string;
  body: VoceFeedAdminUpdate;
}

export function useUpdateVoceFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: UpdateArgs) =>
      apiFetch<VoceFeedAdmin>(`/api/v1/admin/feed/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feed"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

export function usePubblicaVoceFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<VoceFeedAdmin>(`/api/v1/admin/feed/${id}/pubblica`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feed"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

export function useCreaVoceFeedManuale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VoceFeedManualeCreate) =>
      apiFetch<VoceFeedAdmin>("/api/v1/admin/feed/manuale", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feed"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

export function useScartaVoceFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<VoceFeedAdmin>(`/api/v1/admin/feed/${id}/scarta`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feed"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/**
 * Il pulsante «Genera dalle attività»: le bozze-commit diventano voci di diario
 * in lingua-cliente, una per giornata, **lasciate in bozza** da rileggere.
 * Vedi `core/services/genera_attivita.py` per il perché del raggruppamento.
 */
export function useGeneraAttivita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { giorni?: number; max_giornate?: number } = {}) =>
      apiFetch<GeneraAttivitaEsito>("/api/v1/admin/feed/genera", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feed"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/** Pubblica in blocco. Una sola mail al cliente, non una per voce. */
export function usePubblicaMolte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch<AzioneMassaEsito>("/api/v1/admin/feed/pubblica-molte", {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feed"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/** Scarta in blocco. Le voci restano in DB per audit. */
export function useScartaMolte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch<AzioneMassaEsito>("/api/v1/admin/feed/scarta-molte", {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feed"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/**
 * Riporta in bozza voci scartate (o pubblicate: le ritira dalla vetrina).
 * È il ritorno di «Scarta», che fino al 21-08-2026 non ce l'aveva.
 */
export function useRipristinaMolte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch<AzioneMassaEsito>("/api/v1/admin/feed/ripristina-molte", {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feed"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}
